// HPRMS industrial purchase journey — POS /PurchaseOrderManagement paths
import apiClient from "../utilities/apiClient";

export const PR_STATUS = {
  DRAFT: "DRAFT",
  SUBMITTED: "SUBMITTED",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
};

export const RFQ_STATUS = {
  DRAFT: "DRAFT",
  SENT: "SENT",
  WAITING_FOR_RESPONSE: "WAITING_FOR_RESPONSE",
  QUOTATION_SELECTED: "QUOTATION_SELECTED",
  CLOSED: "CLOSED",
};

export const VQ_STATUS = {
  DRAFT: "DRAFT",
  SUBMITTED: "SUBMITTED",
  SELECTED: "SELECTED",
  REJECTED: "REJECTED",
};

const BASE = "/PurchaseOrderManagement";

const toArray = (data) => {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

const getId = (entity) => entity?.POID ?? entity?.id ?? null;

const isInvalidId = (id) => {
  if (id == null) return true;
  const n = String(id).trim().toLowerCase();
  return n === "" || n === "0" || n === "null" || n === "undefined";
};

// ─── Purchase Request ───────────────────────────────────────────────

const mapPrLine = (item) => ({
  ...item,
  POID: getId(item),
  id: getId(item),
  productId: item.productId != null ? String(item.productId) : "",
  productName: item.productName || "",
  quantity: Number(item.quantity) || 0,
  unitOfMeasure: item.unitOfMeasure || "Nos",
  remarks: item.remarks || "",
  sortOrder: item.sortOrder,
});

const mapPurchaseRequest = (pr, lineItems = []) => {
  const id = getId(pr);
  const embedded = pr.purchaseRequestLines || pr.lineItems || [];
  return {
    ...pr,
    POID: id,
    id,
    requestNumber: pr.requestNumber || (id ? `PR-${id}` : ""),
    status: String(pr.status || PR_STATUS.DRAFT).toUpperCase(),
    lineItems: (lineItems.length ? lineItems : embedded).map(mapPrLine),
  };
};

const buildPrBody = (data) => ({
  department: data.department || "",
  requestedBy: data.requestedBy || "",
  neededBy: data.neededBy || data.neededByDate || "",
  notes: data.notes || "",
  branchId: data.branchId || "BR_HEAD",
  requestNumber: data.requestNumber || undefined,
  status: data.status || PR_STATUS.DRAFT,
});

const buildPrLineBody = (purchaseRequestId, item, sortOrder) => ({
  PurchaseRequest_Id: String(purchaseRequestId),
  productId: String(item.productId || ""),
  productName: item.productName || item.name || "",
  unitOfMeasure: item.unitOfMeasure || "Nos",
  quantity: Number(item.quantity) || 0,
  remarks: item.remarks || "",
  sortOrder: sortOrder + 1,
});

export const getPurchaseRequests = async (params = {}) => {
  const {
    status,
    start,
    size,
    silent = false,
    hideLoader = false,
    context = "Loading Purchase Requests",
  } = params;
  const query = [];
  if (status && status !== "ALL") query.push(`status=${encodeURIComponent(status)}`);
  if (start != null) query.push(`start=${encodeURIComponent(start)}`);
  if (size != null) query.push(`size=${encodeURIComponent(size)}`);
  let url = `${BASE}/purchaseRequests`;
  if (query.length) url += `?${query.join("&")}`;
  const data = await apiClient.get(url, {
    context,
    silent,
    hideLoader,
  });
  return toArray(data).map((pr) => mapPurchaseRequest(pr, []));
};

export const getPurchaseRequestById = async (id) => {
  const raw = await apiClient.get(`${BASE}/purchaseRequest/${id}`, {
    context: "Loading Purchase Request",
  });
  const pr = Array.isArray(raw) ? raw[0] : raw;
  if (!pr) return null;
  const lines = await apiClient.get(
    `${BASE}/purchaseRequestLine?purchaseRequestId=${encodeURIComponent(id)}`,
    { context: "Loading PR Lines", silent: true, hideLoader: true }
  );
  return mapPurchaseRequest(pr, toArray(lines));
};

/** Build mapped PR from write payload (no network). */
export const mapPurchaseRequestSnapshot = (id, data = {}, lineItems = []) =>
  mapPurchaseRequest(
    {
      ...data,
      POID: id,
      id,
      status: data.status || PR_STATUS.DRAFT,
      requestNumber: data.requestNumber,
      department: data.department,
      requestedBy: data.requestedBy,
      neededBy: data.neededBy || data.neededByDate,
      notes: data.notes,
    },
    lineItems
  );

export const createPurchaseRequest = async (data) => {
  const createResp = await apiClient.post(`${BASE}/purchaseRequest`, buildPrBody(data), {
    context: "Creating Purchase Request",
  });
  let prId = createResp?.id ?? createResp?.POID;
  if (isInvalidId(prId) && data.requestNumber) {
    const listed = await getPurchaseRequests({ silent: true, hideLoader: true });
    const match = listed.find((p) => p.requestNumber === data.requestNumber);
    prId = match?.POID ?? match?.id;
  }
  if (isInvalidId(prId)) throw new Error("Purchase request created but id was invalid");

  const lines = data.lineItems || [];
  const savedLines = [];
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i].productName && !lines[i].productId) continue;
    const lineResp = await apiClient.post(
      `${BASE}/purchaseRequestLine`,
      buildPrLineBody(prId, lines[i], i),
      { context: "Adding PR Line" }
    );
    const lineId = lineResp?.id ?? lineResp?.POID ?? null;
    savedLines.push(
      mapPrLine({
        ...lines[i],
        POID: lineId,
        id: lineId,
        sortOrder: i + 1,
      })
    );
  }
  // No verify GET-by-id — merge form + write responses
  return mapPurchaseRequestSnapshot(prId, data, savedLines);
};

export const updatePurchaseRequest = async (id, data) => {
  await apiClient.put(`${BASE}/purchaseRequest/${id}`, buildPrBody(data), {
    context: "Updating Purchase Request",
  });
  const existing = await apiClient.get(
    `${BASE}/purchaseRequestLine?purchaseRequestId=${encodeURIComponent(id)}`,
    { silent: true, hideLoader: true }
  );
  const existingLines = toArray(existing);
  const incoming = data.lineItems || [];
  const incomingIds = new Set(
    incoming.map((l) => l.POID ?? l.id).filter(Boolean).map(String)
  );

  const savedLines = [];
  for (let i = 0; i < incoming.length; i++) {
    const item = incoming[i];
    const lineId = item.POID ?? item.id;
    if (lineId) {
      await apiClient.put(
        `${BASE}/purchaseRequestLine/${lineId}`,
        buildPrLineBody(id, item, i),
        { context: "Updating PR Line" }
      );
      savedLines.push(
        mapPrLine({
          ...item,
          POID: lineId,
          id: lineId,
          sortOrder: i + 1,
        })
      );
    } else if (item.productName || item.productId) {
      const lineResp = await apiClient.post(
        `${BASE}/purchaseRequestLine`,
        buildPrLineBody(id, item, i),
        { context: "Adding PR Line" }
      );
      const newId = lineResp?.id ?? lineResp?.POID ?? null;
      savedLines.push(
        mapPrLine({
          ...item,
          POID: newId,
          id: newId,
          sortOrder: i + 1,
        })
      );
    }
  }

  for (const old of existingLines) {
    const oldId = getId(old);
    if (oldId && !incomingIds.has(String(oldId))) {
      await apiClient.delete(`${BASE}/purchaseRequestLine/${oldId}`, {
        context: "Removing PR Line",
        silent: true,
      });
    }
  }
  // No verify GET-by-id — return write snapshot
  return mapPurchaseRequestSnapshot(id, data, savedLines);
};

export const updatePurchaseRequestStatus = async (id, status, current = null) => {
  await apiClient.put(
    `${BASE}/purchaseRequest/${id}/status`,
    { status },
    { context: "Updating PR Status" }
  );
  // No verify GET-by-id — merge status onto current snapshot when provided
  if (current) {
    return mapPurchaseRequest(
      { ...current, POID: id, id, status },
      current.lineItems || []
    );
  }
  return mapPurchaseRequestSnapshot(id, { status }, []);
};

// ─── Request for Quotation ──────────────────────────────────────────

const mapRfqLine = (item) => ({
  ...item,
  POID: getId(item),
  id: getId(item),
  productId: item.productId != null ? String(item.productId) : "",
  productName: item.productName || "",
  quantity: Number(item.quantity) || 0,
  unitOfMeasure: item.unitOfMeasure || "Nos",
  remarks: item.remarks || "",
});

const mapRfq = (rfq, lineItems = [], vendors = []) => {
  const id = getId(rfq);
  const embedded = rfq.requestForQuotationLines || rfq.lineItems || [];
  return {
    ...rfq,
    POID: id,
    id,
    rfqNumber: rfq.rfqNumber || (id ? `RFQ-${id}` : ""),
    status: String(rfq.status || RFQ_STATUS.DRAFT).toUpperCase(),
    purchaseRequestId: rfq.PurchaseRequest_Id ?? rfq.purchaseRequestId ?? null,
    lineItems: (lineItems.length ? lineItems : embedded).map(mapRfqLine),
    vendors,
  };
};

const buildRfqBody = (data) => ({
  branchId: data.branchId || "BR_HEAD",
  validUntil: data.validUntil || "",
  notes: data.notes || "",
  rfqNumber: data.rfqNumber || undefined,
  status: data.status || RFQ_STATUS.DRAFT,
  PurchaseRequest_Id: data.purchaseRequestId
    ? String(data.purchaseRequestId)
    : data.PurchaseRequest_Id
      ? String(data.PurchaseRequest_Id)
      : undefined,
});

const buildRfqLineBody = (rfqId, item, sortOrder) => ({
  RequestForQuotation_Id: String(rfqId),
  productId: String(item.productId || ""),
  productName: item.productName || item.name || "",
  unitOfMeasure: item.unitOfMeasure || "Nos",
  quantity: Number(item.quantity) || 0,
  remarks: item.remarks || "",
  sortOrder: sortOrder + 1,
});

export const getRequestForQuotations = async (params = {}) => {
  const {
    status,
    start,
    size,
    silent = false,
    hideLoader = false,
    context = "Loading RFQs",
  } = params;
  const query = [];
  if (status && status !== "ALL") query.push(`status=${encodeURIComponent(status)}`);
  if (start != null) query.push(`start=${encodeURIComponent(start)}`);
  if (size != null) query.push(`size=${encodeURIComponent(size)}`);
  let url = `${BASE}/requestForQuotations`;
  if (query.length) url += `?${query.join("&")}`;
  const data = await apiClient.get(url, {
    context,
    silent,
    hideLoader,
  });
  return toArray(data).map((r) => mapRfq(r, [], []));
};

export const getRfqById = async (id) => {
  const raw = await apiClient.get(`${BASE}/requestForQuotation/${id}`, {
    context: "Loading RFQ",
  });
  const rfq = Array.isArray(raw) ? raw[0] : raw;
  if (!rfq) return null;
  const [lines, vendors] = await Promise.all([
    apiClient.get(
      `${BASE}/requestForQuotationLine?requestForQuotationId=${encodeURIComponent(id)}`,
      { silent: true, hideLoader: true }
    ),
    apiClient.get(
      `${BASE}/rfqVendor?requestForQuotationId=${encodeURIComponent(id)}`,
      { silent: true, hideLoader: true }
    ),
  ]);
  return mapRfq(rfq, toArray(lines), toArray(vendors));
};

/** Build mapped RFQ from write payload (no network). */
export const mapRfqSnapshot = (id, data = {}, lineItems = [], vendors = []) =>
  mapRfq(
    {
      ...data,
      POID: id,
      id,
      status: data.status || RFQ_STATUS.DRAFT,
      rfqNumber: data.rfqNumber,
      validUntil: data.validUntil,
      notes: data.notes,
      PurchaseRequest_Id: data.purchaseRequestId || data.PurchaseRequest_Id,
      purchaseRequestId: data.purchaseRequestId || data.PurchaseRequest_Id,
    },
    lineItems,
    vendors
  );

export const createRequestForQuotation = async (data) => {
  const createResp = await apiClient.post(`${BASE}/requestForQuotation`, buildRfqBody(data), {
    context: "Creating RFQ",
  });
  let rfqId = createResp?.id ?? createResp?.POID;
  if (isInvalidId(rfqId)) throw new Error("RFQ created but id was invalid");

  const lines = data.lineItems || [];
  const savedLines = [];
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i].productName && !lines[i].productId) continue;
    const lineResp = await apiClient.post(
      `${BASE}/requestForQuotationLine`,
      buildRfqLineBody(rfqId, lines[i], i),
      { context: "Adding RFQ Line" }
    );
    const lineId = lineResp?.id ?? lineResp?.POID ?? null;
    savedLines.push(
      mapRfqLine({
        ...lines[i],
        POID: lineId,
        id: lineId,
      })
    );
  }

  const vendorIds = data.vendorIds || [];
  const savedVendors = [];
  for (const vendorId of vendorIds) {
    const inviteResp = await inviteRfqVendor(rfqId, vendorId);
    savedVendors.push({
      ...(typeof inviteResp === "object" && inviteResp ? inviteResp : {}),
      Vendor_Id: String(vendorId),
      vendorId: String(vendorId),
      RequestForQuotation_Id: String(rfqId),
      status: "INVITED",
    });
  }

  return mapRfqSnapshot(rfqId, data, savedLines, savedVendors);
};

export const updateRequestForQuotation = async (id, data, current = null) => {
  await apiClient.put(`${BASE}/requestForQuotation/${id}`, buildRfqBody(data), {
    context: "Updating RFQ",
  });
  if (current) {
    return mapRfq(
      { ...current, ...data, POID: id, id },
      current.lineItems || data.lineItems || [],
      current.vendors || []
    );
  }
  return mapRfqSnapshot(id, data, data.lineItems || [], []);
};

export const updateRfqStatus = async (id, status, current = null) => {
  await apiClient.put(
    `${BASE}/requestForQuotation/${id}/status`,
    { status },
    { context: "Updating RFQ Status" }
  );
  if (current) {
    return mapRfq(
      { ...current, POID: id, id, status },
      current.lineItems || [],
      current.vendors || []
    );
  }
  return mapRfqSnapshot(id, { status }, [], []);
};

export const inviteRfqVendor = async (rfqId, vendorId, notes = "") => {
  return apiClient.post(
    `${BASE}/rfqVendor`,
    {
      RequestForQuotation_Id: String(rfqId),
      Vendor_Id: String(vendorId),
      status: "INVITED",
      notes,
    },
    { context: "Inviting Vendor to RFQ" }
  );
};

export const getRfqVendors = async (rfqId) => {
  const data = await apiClient.get(
    `${BASE}/rfqVendor?requestForQuotationId=${encodeURIComponent(rfqId)}`,
    { context: "Loading RFQ Vendors", silent: true, hideLoader: true }
  );
  return toArray(data);
};

// ─── Vendor Quotation ───────────────────────────────────────────────

const mapVqLine = (item) => ({
  ...item,
  POID: getId(item),
  id: getId(item),
  productId: item.productId != null ? String(item.productId) : "",
  productName: item.productName || "",
  quantity: Number(item.quantity) || 0,
  unitPrice: Number(item.unitPrice ?? item.price) || 0,
  discountAmount: Number(item.discountAmount) || 0,
  taxRate: Number(item.taxRate) || 0,
  taxAmount: Number(item.taxAmount) || 0,
  lineTotal: Number(item.lineTotal) || 0,
  deliveryDays: item.deliveryDays != null ? Number(item.deliveryDays) : null,
  unitOfMeasure: item.unitOfMeasure || "Nos",
});

const mapVendorQuotation = (vq, lineItems = []) => {
  const id = getId(vq);
  const embedded = vq.vendorQuotationLines || vq.lineItems || [];
  const notes = vq.notes || "";
  const poFromNotes = String(notes).match(/PO_ID:([A-Za-z0-9_-]+)/i);
  const purchaseOrderId =
    vq.PurchaseOrder_Id ??
    vq.purchaseOrderId ??
    (poFromNotes ? poFromNotes[1] : null);
  return {
    ...vq,
    POID: id,
    id,
    quotationNumber: vq.quotationNumber || (id ? `VQ-${id}` : ""),
    status: String(vq.status || VQ_STATUS.DRAFT).toUpperCase(),
    vendorId: vq.Vendor_Id ?? vq.vendorId ?? null,
    rfqId: vq.RequestForQuotation_Id ?? vq.rfqId ?? null,
    purchaseOrderId: purchaseOrderId != null ? String(purchaseOrderId) : null,
    notes,
    subtotal: vq.subTotal ?? vq.subtotal ?? 0,
    discountAmount: vq.discountAmount ?? 0,
    taxAmount: vq.taxAmount ?? 0,
    totalAmount: vq.total ?? vq.totalAmount ?? 0,
    deliveryDays: vq.deliveryDays,
    paymentTerms: vq.paymentTerms || "",
    deliveryTerms: vq.deliveryTerms || "",
    lineItems: (lineItems.length ? lineItems : embedded).map(mapVqLine),
  };
};

const buildVqBody = (data) => {
  const lines = data.lineItems || [];
  const subTotal = Number(
    data.subtotal ??
      data.subTotal ??
      lines.reduce(
        (s, l) => s + Number(l.quantity || 0) * Number((l.unitPrice ?? l.price) || 0),
        0
      )
  );
  const discountAmount = Number(data.discountAmount) || 0;
  const taxAmount = Number(data.taxAmount ?? data.tax) || 0;
  const total = Number(
    data.totalAmount ?? data.total ?? (subTotal - discountAmount + taxAmount)
  );

  return {
    Vendor_Id: String(data.vendorId ?? data.Vendor_Id ?? ""),
    RequestForQuotation_Id: String(data.rfqId ?? data.RequestForQuotation_Id ?? ""),
    quotationNumber: data.quotationNumber || undefined,
    status: data.status || VQ_STATUS.DRAFT,
    validUntil: data.validUntil || "",
    currencyCode: data.currencyCode || "",
    paymentTerms: data.paymentTerms || "",
    deliveryTerms: data.deliveryTerms || "",
    deliveryDays: data.deliveryDays != null ? Number(data.deliveryDays) : null,
    notes: data.notes || "",
    subTotal,
    discountAmount,
    taxAmount,
    total,
  };
};

const buildVqLineBody = (vqId, item, sortOrder) => {
  const qty = Number(item.quantity) || 0;
  const unitPrice = Number(item.unitPrice ?? item.price) || 0;
  const discountAmount = Number(item.discountAmount) || 0;
  const taxAmount = Number(item.taxAmount) || 0;
  const lineTotal = Number(item.lineTotal ?? qty * unitPrice - discountAmount + taxAmount);
  return {
    VendorQuotation_Id: String(vqId),
    productId: String(item.productId || ""),
    productName: item.productName || item.name || "",
    unitOfMeasure: item.unitOfMeasure || "Nos",
    quantity: qty,
    unitPrice,
    discountAmount,
    taxRate: Number(item.taxRate) || 0,
    taxAmount,
    lineTotal,
    deliveryDays: item.deliveryDays != null ? Number(item.deliveryDays) : null,
    sortOrder: sortOrder + 1,
  };
};

export const getVendorQuotations = async (params = {}) => {
  const {
    rfqId,
    status,
    silent = false,
    hideLoader = false,
    context = "Loading Vendor Quotations",
  } = params;
  const query = [];
  if (rfqId) query.push(`RequestForQuotation_Id=${encodeURIComponent(rfqId)}`);
  if (status && status !== "ALL") query.push(`status=${encodeURIComponent(status)}`);
  let url = `${BASE}/vendorQuotations`;
  if (query.length) url += `?${query.join("&")}`;
  const data = await apiClient.get(url, {
    context,
    silent,
    hideLoader,
  });
  return toArray(data).map((v) => mapVendorQuotation(v, []));
};

export const getVendorQuotationById = async (id) => {
  const raw = await apiClient.get(`${BASE}/vendorQuotation/${id}`, {
    context: "Loading Vendor Quotation",
  });
  const vq = Array.isArray(raw) ? raw[0] : raw;
  if (!vq) return null;
  const lines = await apiClient.get(
    `${BASE}/vendorQuotationLine?vendorQuotationId=${encodeURIComponent(id)}`,
    { silent: true, hideLoader: true }
  );
  return mapVendorQuotation(vq, toArray(lines));
};

/** Build mapped VQ from write payload (no network). */
export const mapVendorQuotationSnapshot = (id, data = {}, lineItems = []) =>
  mapVendorQuotation(
    {
      ...data,
      POID: id,
      id,
      status: data.status || VQ_STATUS.DRAFT,
      Vendor_Id: data.vendorId || data.Vendor_Id,
      vendorId: data.vendorId || data.Vendor_Id,
      RequestForQuotation_Id: data.rfqId || data.RequestForQuotation_Id,
      rfqId: data.rfqId || data.RequestForQuotation_Id,
      quotationNumber: data.quotationNumber,
      notes: data.notes,
      paymentTerms: data.paymentTerms,
      deliveryTerms: data.deliveryTerms,
      deliveryDays: data.deliveryDays,
      subTotal: data.subtotal ?? data.subTotal,
      discountAmount: data.discountAmount,
      taxAmount: data.taxAmount ?? data.tax,
      total: data.totalAmount ?? data.total,
    },
    lineItems
  );

export const createVendorQuotation = async (data) => {
  const createResp = await apiClient.post(`${BASE}/vendorQuotation`, buildVqBody(data), {
    context: "Creating Vendor Quotation",
  });
  let vqId = createResp?.id ?? createResp?.POID;
  if (isInvalidId(vqId)) throw new Error("Vendor quotation created but id was invalid");

  const lines = data.lineItems || [];
  const savedLines = [];
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i].productName && !lines[i].productId) continue;
    const lineResp = await apiClient.post(
      `${BASE}/vendorQuotationLine`,
      buildVqLineBody(vqId, lines[i], i),
      { context: "Adding VQ Line" }
    );
    const lineId = lineResp?.id ?? lineResp?.POID ?? null;
    savedLines.push(
      mapVqLine({
        ...lines[i],
        POID: lineId,
        id: lineId,
        unitPrice: lines[i].unitPrice ?? lines[i].price,
      })
    );
  }
  return mapVendorQuotationSnapshot(vqId, data, savedLines);
};

export const updateVendorQuotation = async (id, data, current = null) => {
  await apiClient.put(`${BASE}/vendorQuotation/${id}`, buildVqBody(data), {
    context: "Updating Vendor Quotation",
  });
  if (current) {
    return mapVendorQuotation(
      { ...current, ...data, POID: id, id },
      data.lineItems || current.lineItems || []
    );
  }
  return mapVendorQuotationSnapshot(id, data, data.lineItems || []);
};

export const updateVendorQuotationStatus = async (id, status, current = null) => {
  await apiClient.put(
    `${BASE}/vendorQuotation/${id}/status`,
    { status },
    { context: "Updating VQ Status" }
  );
  if (current) {
    return mapVendorQuotation(
      { ...current, POID: id, id, status },
      current.lineItems || []
    );
  }
  return mapVendorQuotationSnapshot(id, { status }, []);
};

export const selectVendorQuotation = async (id, current = null) => {
  await apiClient.post(`${BASE}/vendorQuotation/${id}/select`, {}, {
    context: "Selecting Vendor Quotation",
  });
  if (current) {
    return mapVendorQuotation(
      { ...current, POID: id, id, status: VQ_STATUS.SELECTED },
      current.lineItems || []
    );
  }
  return mapVendorQuotationSnapshot(id, { status: VQ_STATUS.SELECTED }, []);
};

export const convertVendorQuotationToPurchaseOrder = async (id) => {
  return apiClient.post(
    `${BASE}/vendorQuotation/${id}/convertToPurchaseOrder`,
    {},
    { context: "Converting to Purchase Order" }
  );
};

/** Optional lifecycle update on existing PO — same POS path as Accept/Reject. */
export const updatePurchaseOrder = async (id, body) => {
  return apiClient.put(`${BASE}/purchaseOrder/${id}`, body, {
    context: "Updating Purchase Order",
  });
};
